import { useEffect, useRef, useState } from 'preact/hooks';

interface GraphNode {
  id: string;
  label: string;
  sub: string;
  type: 'issue' | 'commit' | 'user' | 'wiki';
  status?: string;
  reporter_id?: number;
}

interface GraphEdge {
  source: string;
  target: string;
  type: 'issue' | 'commit' | 'user' | 'wiki';
}

interface KnowledgeGraphProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

interface LiveNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
  scale: number;
  expanded: boolean;
  appearing: boolean;
  appearT: number;
  targetX?: number;
  targetY?: number;
}

const STATUS_COLORS: Record<string, string> = {
  new: '#3b82f6',
  in_progress: '#f59e0b',
  review: '#8b5cf6',
  resolved: '#10b981',
  rejected: '#ef4444',
  on_hold: '#6b7280',
  updated: '#06b6d4',
};

const TYPE_COLORS: Record<string, string> = {
  commit: '#6b7280',
  user: '#8b5cf6',
  wiki: '#d97706',
};

const EDGE_COLORS: Record<string, string> = {
  issue: 'rgba(59,130,246,{a})',
  commit: 'rgba(107,114,128,{a})',
  user: 'rgba(139,92,246,{a})',
  wiki: 'rgba(217,169,22,{a})',
};

const NODE_RADIUS: Record<string, number> = {
  issue: 32,
  commit: 18,
  user: 16,
  wiki: 14,
};

function getNodeColor(node: GraphNode): string {
  if (node.type === 'issue') {
    return STATUS_COLORS[node.status || 'new'] || '#3b82f6';
  }
  return TYPE_COLORS[node.type] || '#6b7280';
}

function easeOutBack(t: number): number {
  const c1 = 1.70158, c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

export function KnowledgeGraph({ nodes: graphNodes, edges: graphEdges }: KnowledgeGraphProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<{
    nodes: Record<string, LiveNode>;
    visibleIds: Set<string>;
    selectedId: string | null;
    zoom: number;
    camX: number;
    camY: number;
    dragging: { id: string } | null;
    dragOffX: number;
    dragOffY: number;
    isPanning: boolean;
    panStartX: number;
    panStartY: number;
    mouseDownX: number;
    mouseDownY: number;
    hiddenTypes: Set<string>;
    W: number;
    H: number;
    animId: number;
    timeNow: number;
  }>({
    nodes: {},
    visibleIds: new Set(),
    selectedId: null,
    zoom: 1,
    camX: 0,
    camY: 0,
    dragging: null,
    dragOffX: 0,
    dragOffY: 0,
    isPanning: false,
    panStartX: 0,
    panStartY: 0,
    mouseDownX: 0,
    mouseDownY: 0,
    hiddenTypes: new Set(),
    W: 0,
    H: 0,
    animId: 0,
    timeNow: 0,
  });

  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());

  const nodeMap = useRef<Record<string, GraphNode>>({});

  // Build nodeMap
  useEffect(() => {
    const m: Record<string, GraphNode> = {};
    graphNodes.forEach(n => { m[n.id] = n; });
    nodeMap.current = m;
  }, [graphNodes]);

  function getNeighbors(nodeId: string): Set<string> {
    const ids = new Set<string>();
    graphEdges.forEach(e => {
      if (e.source === nodeId) ids.add(e.target);
      if (e.target === nodeId) ids.add(e.source);
    });
    return ids;
  }

  function getConnectedSet(nodeId: string) {
    const cn = new Set<string>([nodeId]);
    const ce = new Set<number>();
    graphEdges.forEach((e, i) => {
      const s = stateRef.current;
      if (!s.visibleIds.has(e.source) || !s.visibleIds.has(e.target)) return;
      if (e.source === nodeId || e.target === nodeId) {
        cn.add(e.source);
        cn.add(e.target);
        ce.add(i);
      }
    });
    return { cn, ce };
  }

  function initGraph() {
    const s = stateRef.current;
    s.nodes = {};
    s.visibleIds = new Set();
    s.selectedId = null;

    const issueIds = graphNodes.filter(n => n.type === 'issue').map(n => n.id);
    const cx = s.W / 2;
    const cy = s.H / 2;

    issueIds.forEach((id, i) => {
      const angle = (i / issueIds.length) * Math.PI * 2 - Math.PI / 2;
      const r = 160 + Math.random() * 40;
      s.visibleIds.add(id);
      s.nodes[id] = {
        x: cx + Math.cos(angle) * r,
        y: cy + Math.sin(angle) * r,
        vx: 0, vy: 0, scale: 1, expanded: false,
        appearing: false, appearT: 1,
      };
    });
  }

  function toggleExpand(nodeId: string) {
    const s = stateRef.current;
    const ns = s.nodes[nodeId];
    if (!ns) return;
    ns.expanded = !ns.expanded;

    if (ns.expanded) {
      const neighbors = getNeighbors(nodeId);
      const newIds = [...neighbors].filter(id =>
        !s.visibleIds.has(id) && !s.hiddenTypes.has(nodeMap.current[id]?.type)
      );
      newIds.forEach((id, i) => {
        const angle = (i / newIds.length) * Math.PI * 2 + Math.random() * 0.4;
        const dist = 70 + Math.random() * 30;
        s.visibleIds.add(id);
        s.nodes[id] = {
          x: ns.x, y: ns.y,
          vx: 0, vy: 0, scale: 0,
          expanded: false, appearing: true, appearT: 0,
          targetX: ns.x + Math.cos(angle) * dist,
          targetY: ns.y + Math.sin(angle) * dist,
        };
      });
    } else {
      const neighbors = getNeighbors(nodeId);
      neighbors.forEach(nid => {
        const nd = nodeMap.current[nid];
        if (nd && nd.type !== 'issue') {
          const otherExpanded = [...getNeighbors(nid)].filter(
            oid => oid !== nodeId && s.visibleIds.has(oid) && s.nodes[oid]?.expanded
          );
          if (otherExpanded.length === 0) {
            s.visibleIds.delete(nid);
            delete s.nodes[nid];
          }
        }
      });
    }
  }

  function simulate() {
    const s = stateRef.current;
    const springLen = 110;
    const springK = 0.004;
    const repulsion = 3000;
    const damping = 0.9;
    const centerPull = 0.00015;

    const vis = [...s.visibleIds].filter(id => s.nodes[id]).map(id => ({ id, n: s.nodes[id] }));

    for (let i = 0; i < vis.length; i++) {
      for (let j = i + 1; j < vis.length; j++) {
        const a = vis[i].n, b = vis[j].n;
        const dx = b.x - a.x, dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = repulsion / (dist * dist);
        const fx = (dx / dist) * force, fy = (dy / dist) * force;
        a.vx -= fx; a.vy -= fy;
        b.vx += fx; b.vy += fy;
      }
    }

    graphEdges.forEach(e => {
      if (!s.visibleIds.has(e.source) || !s.visibleIds.has(e.target)) return;
      const a = s.nodes[e.source], b = s.nodes[e.target];
      if (!a || !b) return;
      const dx = b.x - a.x, dy = b.y - a.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const force = (dist - springLen) * springK;
      const fx = (dx / dist) * force, fy = (dy / dist) * force;
      a.vx += fx; a.vy += fy;
      b.vx -= fx; b.vy -= fy;
    });

    vis.forEach(({ n }) => {
      if (s.dragging && n === s.nodes[s.dragging.id]) return;
      n.vx += (s.W / 2 - n.x) * centerPull;
      n.vy += (s.H / 2 - n.y) * centerPull;
      n.vx += (Math.random() - 0.5) * 0.12;
      n.vy += (Math.random() - 0.5) * 0.12;
      n.vx *= damping;
      n.vy *= damping;

      if (n.appearing) {
        n.appearT = Math.min(1, n.appearT + 0.045);
        n.scale = easeOutBack(n.appearT);
        n.x += (n.targetX! - n.x) * 0.1;
        n.y += (n.targetY! - n.y) * 0.1;
        if (n.appearT >= 1) n.appearing = false;
      } else {
        n.x += n.vx;
        n.y += n.vy;
      }
    });
  }

  function screenToWorld(mx: number, my: number) {
    const s = stateRef.current;
    return {
      x: (mx - s.W / 2) / s.zoom + s.W / 2 - s.camX,
      y: (my - s.H / 2) / s.zoom + s.H / 2 - s.camY,
    };
  }

  function hitTest(mx: number, my: number): string | null {
    const s = stateRef.current;
    const p = screenToWorld(mx, my);
    let best: string | null = null, bestDist = Infinity;
    s.visibleIds.forEach(id => {
      const n = s.nodes[id];
      const d = nodeMap.current[id];
      if (!n || !d) return;
      const dx = p.x - n.x, dy = p.y - n.y;
      const dist = dx * dx + dy * dy;
      const r = (NODE_RADIUS[d.type] || 16) * n.scale + 6;
      if (dist < r * r && dist < bestDist) {
        best = id;
        bestDist = dist;
      }
    });
    return best;
  }

  function draw(ts: number) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const s = stateRef.current;
    s.timeNow = ts || 0;

    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, s.W, s.H);

    ctx.save();
    ctx.translate(s.W / 2, s.H / 2);
    ctx.scale(s.zoom, s.zoom);
    ctx.translate(-s.W / 2 + s.camX, -s.H / 2 + s.camY);

    // Grid dots
    const gridSize = 40;
    const startX = Math.floor((-s.camX + s.W / 2 - s.W / (2 * s.zoom)) / gridSize) * gridSize;
    const startY = Math.floor((-s.camY + s.H / 2 - s.H / (2 * s.zoom)) / gridSize) * gridSize;
    const endX = startX + s.W / s.zoom + gridSize;
    const endY = startY + s.H / s.zoom + gridSize;
    ctx.fillStyle = 'rgba(0,0,0,0.04)';
    for (let gx = startX; gx < endX; gx += gridSize) {
      for (let gy = startY; gy < endY; gy += gridSize) {
        ctx.beginPath();
        ctx.arc(gx, gy, 0.8, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const conn = s.selectedId ? getConnectedSet(s.selectedId) : null;

    // Edges
    graphEdges.forEach((e, i) => {
      if (!s.visibleIds.has(e.source) || !s.visibleIds.has(e.target)) return;
      const a = s.nodes[e.source], b = s.nodes[e.target];
      if (!a || !b) return;

      const isHL = conn ? conn.ce.has(i) : false;
      const alpha = conn ? (isHL ? 0.6 : 0.06) : 0.25;
      const width = conn ? (isHL ? 2.5 : 0.5) : 1.2;
      const dashes = e.type === 'commit' || e.type === 'wiki';

      ctx.beginPath();
      if (dashes) ctx.setLineDash([5, 3]);
      else ctx.setLineDash([]);
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.strokeStyle = EDGE_COLORS[e.type]?.replace('{a}', String(alpha)) || `rgba(100,100,100,${alpha})`;
      ctx.lineWidth = width;
      ctx.stroke();
      ctx.setLineDash([]);

      if (isHL) {
        const t = ((s.timeNow * 0.0005) % 1 + Math.random() * 0.01) % 1;
        const px = a.x + (b.x - a.x) * t;
        const py = a.y + (b.y - a.y) * t;
        ctx.beginPath();
        ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = EDGE_COLORS[e.type]?.replace('{a}', '0.8') || 'rgba(100,100,100,0.8)';
        ctx.fill();
      }
    });

    // Nodes
    [...s.visibleIds].forEach(id => {
      const n = s.nodes[id];
      const d = nodeMap.current[id];
      if (!n || !d) return;

      const rad = (NODE_RADIUS[d.type] || 16) * n.scale;
      if (rad < 0.5) return;
      const color = getNodeColor(d);
      const isSelected = id === s.selectedId;
      const isConn = conn ? conn.cn.has(id) : false;
      const alpha = conn ? (isConn ? 1 : 0.12) : 1;

      ctx.globalAlpha = alpha;

      // Shadow
      if (alpha > 0.5) {
        ctx.beginPath();
        ctx.arc(n.x + 1, n.y + 2, rad + 2, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,0,0,0.06)';
        ctx.fill();
      }

      // Glow
      if (isSelected || (n.expanded && alpha > 0.5)) {
        const glowR = rad * (isSelected ? 2.5 : 2);
        const grad = ctx.createRadialGradient(n.x, n.y, rad * 0.3, n.x, n.y, glowR);
        grad.addColorStop(0, color + '20');
        grad.addColorStop(1, color + '00');
        ctx.beginPath();
        ctx.arc(n.x, n.y, glowR, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      }

      // Pulsing ring
      if (n.expanded && alpha > 0.5) {
        const pulse = 1 + Math.sin(s.timeNow * 0.003) * 0.12;
        ctx.beginPath();
        ctx.arc(n.x, n.y, rad * pulse * 1.5, 0, Math.PI * 2);
        ctx.strokeStyle = color + '30';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Node body
      const grad = ctx.createRadialGradient(n.x - rad * 0.25, n.y - rad * 0.25, rad * 0.1, n.x, n.y, rad);
      grad.addColorStop(0, color + 'ff');
      grad.addColorStop(1, color + 'cc');
      ctx.beginPath();
      ctx.arc(n.x, n.y, rad, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();

      // Border
      if (d.type === 'issue') {
        ctx.strokeStyle = isSelected ? '#1e293b' : (n.expanded ? color : color + '80');
        ctx.lineWidth = isSelected ? 2.5 : (n.expanded ? 2 : 1.5);
      } else {
        ctx.strokeStyle = isSelected ? '#1e293b' : (n.expanded ? color : 'rgba(255,255,255,0.6)');
        ctx.lineWidth = isSelected ? 2.5 : (n.expanded ? 1.5 : 0.5);
      }
      ctx.stroke();

      // Label
      if (n.scale > 0.4) {
        ctx.textAlign = 'center';
        if (d.type === 'issue') {
          ctx.font = `bold ${Math.round(11 * n.scale)}px system-ui`;
          ctx.fillStyle = '#1e293b';
          ctx.fillText(d.label, n.x, n.y - 4);
          ctx.font = `${Math.round(9 * n.scale)}px system-ui`;
          ctx.fillStyle = '#334155';
          const short = d.sub.length > 14 ? d.sub.substring(0, 14) + '\u2026' : d.sub;
          ctx.fillText(short, n.x, n.y + 10);
        } else {
          ctx.font = `${Math.round(9 * n.scale)}px system-ui`;
          ctx.fillStyle = '#ffffff';
          ctx.fillText(d.label, n.x, n.y + 1);
        }
        if (d.type !== 'issue' && n.scale > 0.7) {
          const typeLabels: Record<string, string> = { commit: '커밋', user: '작성자', wiki: '위키' };
          ctx.font = `${Math.round(8 * n.scale)}px system-ui`;
          ctx.fillStyle = '#94a3b8';
          ctx.fillText(typeLabels[d.type] || '', n.x, n.y + rad + 13);
        }
      }

      ctx.globalAlpha = 1;
    });

    ctx.restore();
    simulate();
    s.animId = requestAnimationFrame(draw);
  }

  // Setup canvas and events
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const s = stateRef.current;

    function resize() {
      const parent = canvas!.parentElement;
      if (!parent) return;
      s.W = canvas!.width = parent.clientWidth;
      s.H = canvas!.height = parent.clientHeight;
    }

    resize();
    initGraph();
    s.animId = requestAnimationFrame(draw);

    const onResize = () => { resize(); };
    window.addEventListener('resize', onResize);

    const onMouseDown = (e: MouseEvent) => {
      const rect = canvas!.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      s.mouseDownX = mx;
      s.mouseDownY = my;

      const hit = hitTest(mx, my);
      if (hit) {
        s.dragging = { id: hit };
        const p = screenToWorld(mx, my);
        s.dragOffX = s.nodes[hit].x - p.x;
        s.dragOffY = s.nodes[hit].y - p.y;
        s.nodes[hit].vx = 0;
        s.nodes[hit].vy = 0;
        canvas!.classList.add('cursor-grabbing');
      } else {
        s.isPanning = true;
        s.panStartX = mx;
        s.panStartY = my;
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas!.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      if (s.dragging) {
        const p = screenToWorld(mx, my);
        const n = s.nodes[s.dragging.id];
        if (n) {
          n.x = p.x + s.dragOffX;
          n.y = p.y + s.dragOffY;
          n.vx = 0;
          n.vy = 0;
        }
      } else if (s.isPanning) {
        s.camX += (mx - s.panStartX) / s.zoom;
        s.camY += (my - s.panStartY) / s.zoom;
        s.panStartX = mx;
        s.panStartY = my;
      } else {
        const hit = hitTest(mx, my);
        canvas!.style.cursor = hit ? 'pointer' : 'grab';
      }
    };

    const onMouseUp = (e: MouseEvent) => {
      const rect = canvas!.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const wasDrag = Math.abs(mx - s.mouseDownX) > 4 || Math.abs(my - s.mouseDownY) > 4;

      if (!wasDrag) {
        const hit = hitTest(mx, my);
        if (hit) {
          s.selectedId = s.selectedId === hit ? null : hit;
          if (s.selectedId) {
            toggleExpand(hit);
            setSelectedNode(nodeMap.current[hit] || null);
          } else {
            setSelectedNode(null);
          }
        } else {
          s.selectedId = null;
          setSelectedNode(null);
        }
      }
      s.dragging = null;
      s.isPanning = false;
      canvas!.classList.remove('cursor-grabbing');
    };

    const onContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      initGraph();
      s.selectedId = null;
      setSelectedNode(null);
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 0.93 : 1.07;
      s.zoom = Math.max(0.25, Math.min(4, s.zoom * factor));
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('contextmenu', onContextMenu);
    canvas.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      cancelAnimationFrame(s.animId);
      window.removeEventListener('resize', onResize);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('contextmenu', onContextMenu);
      canvas.removeEventListener('wheel', onWheel);
    };
  }, [graphNodes, graphEdges]);

  // Handle filter changes
  useEffect(() => {
    const s = stateRef.current;
    s.hiddenTypes = hiddenTypes;

    [...s.visibleIds].forEach(id => {
      const d = nodeMap.current[id];
      if (d && d.type !== 'issue' && hiddenTypes.has(d.type)) {
        s.visibleIds.delete(id);
        delete s.nodes[id];
      }
    });
    [...s.visibleIds].forEach(id => {
      if (s.nodes[id]?.expanded) {
        s.nodes[id].expanded = false;
        toggleExpand(id);
      }
    });
  }, [hiddenTypes]);

  function toggleFilter(type: string) {
    setHiddenTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  const STATUS_LABELS: Record<string, string> = {
    new: '신규', in_progress: '진행중', review: '검토', resolved: '해결됨',
    rejected: '반려', on_hold: '보류', updated: '업데이트',
  };

  const connectedNodes = selectedNode
    ? [...getNeighbors(selectedNode.id)]
        .filter(id => stateRef.current.visibleIds.has(id))
        .map(id => nodeMap.current[id])
        .filter(Boolean)
    : [];

  return (
    <div class="relative w-full" style={{ height: '600px' }}>
      <canvas
        ref={canvasRef}
        class="w-full h-full rounded-lg border border-gray-200"
        style={{ cursor: 'grab', background: '#f8fafc' }}
      />

      {/* Controls */}
      <div class="absolute top-3 left-3 bg-white/90 backdrop-blur border border-gray-200 rounded-lg px-3 py-2.5 shadow-sm">
        <div class="text-[10px] text-gray-400 uppercase tracking-wider mb-2 font-medium">표시 필터</div>
        {[
          { type: 'issue', label: '이슈', color: '#3b82f6' },
          { type: 'commit', label: '커밋', color: '#6b7280' },
          { type: 'user', label: '작성자', color: '#8b5cf6' },
          { type: 'wiki', label: '위키', color: '#d97706' },
        ].map(f => (
          <label key={f.type} class="flex items-center gap-2 text-xs text-gray-600 cursor-pointer py-0.5 hover:text-gray-900">
            <input
              type="checkbox"
              checked={!hiddenTypes.has(f.type)}
              onChange={() => toggleFilter(f.type)}
              class="accent-blue-500 w-3 h-3"
            />
            <span class="w-2 h-2 rounded-full inline-block" style={{ background: f.color }} />
            {f.label}
          </label>
        ))}
      </div>

      {/* Legend */}
      <div class="absolute bottom-3 left-3 bg-white/90 backdrop-blur border border-gray-200 rounded-lg px-3 py-2.5 shadow-sm">
        <div class="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5 font-medium">연결선</div>
        <div class="flex flex-col gap-1 text-[11px] text-gray-500">
          <div class="flex items-center gap-2"><span class="w-4 border-t-2 border-blue-400 inline-block" /> 이슈 연결</div>
          <div class="flex items-center gap-2"><span class="w-4 border-t-2 border-dashed border-gray-400 inline-block" /> 커밋 연결</div>
          <div class="flex items-center gap-2"><span class="w-4 border-t border-purple-400 inline-block" /> 작성자</div>
          <div class="flex items-center gap-2"><span class="w-4 border-t-2 border-dashed border-amber-500 inline-block" /> 위키 연결</div>
        </div>
      </div>

      {/* Info Panel */}
      {selectedNode && (
        <div class="absolute top-3 right-3 bg-white/95 backdrop-blur border border-gray-200 rounded-xl w-60 shadow-lg overflow-hidden">
          <div class="p-3 border-b border-gray-100">
            <div class="flex items-center justify-between">
              <span
                class="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded"
                style={{
                  background: getNodeColor(selectedNode) + '18',
                  color: getNodeColor(selectedNode),
                }}
              >
                {{ issue: '이슈', commit: '커밋', user: '작성자', wiki: '위키' }[selectedNode.type]}
              </span>
              <button
                onClick={() => { stateRef.current.selectedId = null; setSelectedNode(null); }}
                class="text-gray-400 hover:text-gray-700 text-sm"
              >
                &#x2715;
              </button>
            </div>
            <h3 class="text-sm font-semibold text-gray-900 mt-1">{selectedNode.label}</h3>
            {selectedNode.sub && <p class="text-xs text-gray-500 mt-0.5">{selectedNode.sub}</p>}
          </div>
          {selectedNode.type === 'issue' && selectedNode.status && (
            <div class="px-3 py-2 text-xs flex justify-between border-b border-gray-100">
              <span class="text-gray-400">상태</span>
              <span class="font-medium" style={{ color: getNodeColor(selectedNode) }}>
                {STATUS_LABELS[selectedNode.status] || selectedNode.status}
              </span>
            </div>
          )}
          {connectedNodes.length > 0 && (
            <div class="px-3 py-2">
              <div class="text-[10px] text-gray-400 uppercase tracking-wider mb-1">연결 ({connectedNodes.length})</div>
              <div class="max-h-32 overflow-y-auto space-y-0.5">
                {connectedNodes.map(nd => (
                  <div
                    key={nd.id}
                    class="text-xs text-blue-500 hover:text-blue-700 cursor-pointer flex items-center gap-1.5 py-0.5"
                    onClick={() => {
                      const s = stateRef.current;
                      s.selectedId = nd.id;
                      if (s.nodes[nd.id] && !s.nodes[nd.id].expanded) toggleExpand(nd.id);
                      setSelectedNode(nd);
                    }}
                  >
                    <span class="w-1.5 h-1.5 rounded-full" style={{ background: getNodeColor(nd) }} />
                    {nd.label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Hint */}
      <div class="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs text-gray-400 bg-white/80 px-3 py-1 rounded border border-gray-100">
        클릭: 연결 펼치기 · 드래그: 이동 · 스크롤: 줌 · 우클릭: 초기화
      </div>
    </div>
  );
}
